import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { decodeEventLog, formatUnits, type Address, type Hex } from "viem";

import { Alert, Button, CopyAction, PageHeader, ProofStamp, ReviewRow } from "@/components/ui";
import { browserPublicClient } from "@/lib/chain.client";
import { CLAIM_CONTRACT, NETWORK_NAME, TOKEN_DECIMALS, TOKEN_SYMBOL, explorerAddress, explorerTx } from "@/lib/config";
import { swapAbi } from "@/lib/contracts";
import { formatTimestamp, shortenAddress } from "@/lib/format";
import { usePageMeta } from "@/lib/page-meta";

type VerifiedTransaction = {
  hash: Hex;
  wallet: Address;
  amount: bigint;
  blockNumber: bigint;
  timestamp: bigint | null;
};

type LookupState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "verified"; transaction: VerifiedTransaction }
  | { status: "failed"; message: string };

const TX_HASH = /^0x[0-9a-fA-F]{64}$/;
const WALLET_ADDRESS = /^0x[0-9a-fA-F]{40}$/;

export function VerificationHubPage() {
  usePageMeta({
    title: "Verifikasi On-chain",
    description: "Verifikasi transaksi migrasi atau catatan UtilityRegistry AsetQu.",
  });

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initial = params.get("tx") ?? "";
  const [query, setQuery] = useState(initial);
  const [lookup, setLookup] = useState<LookupState>({ status: "idle" });

  const verifyTransaction = useCallback(async (hash: Hex) => {
    if (!CLAIM_CONTRACT) {
      setLookup({ status: "failed", message: "Kontrak migrasi resmi belum dikonfigurasi." });
      return;
    }

    setLookup({ status: "loading" });

    try {
      const receipt = await browserPublicClient.getTransactionReceipt({ hash });
      if (receipt.status !== "success") {
        setLookup({
          status: "failed",
          message: "Transaksi ditemukan, tetapi gagal dieksekusi. Migrasi tidak terkonfirmasi.",
        });
        return;
      }

      if (!receipt.to || receipt.to.toLowerCase() !== CLAIM_CONTRACT.toLowerCase()) {
        setLookup({
          status: "failed",
          message: "Transaksi ini tidak ditujukan ke kontrak migrasi resmi AsetQu.",
        });
        return;
      }

      let swap: { account: Address; amount: bigint } | null = null;
      for (const log of receipt.logs) {
        if (log.address.toLowerCase() !== CLAIM_CONTRACT.toLowerCase()) continue;
        try {
          const decoded = decodeEventLog({
            abi: swapAbi,
            eventName: "Swapped",
            data: log.data,
            topics: log.topics,
          });
          swap = decoded.args as { account: Address; amount: bigint };
          break;
        } catch {
          // A receipt contains transfer and approval logs too; only Swapped is proof.
        }
      }

      if (!swap) {
        setLookup({
          status: "failed",
          message: "Transaksi menuju kontrak resmi, tetapi tidak memiliki event migrasi yang valid.",
        });
        return;
      }

      let timestamp: bigint | null = null;
      try {
        timestamp = (await browserPublicClient.getBlock({ blockNumber: receipt.blockNumber })).timestamp;
      } catch {
        // Block time is supporting metadata; the confirmed receipt remains authoritative.
      }

      setLookup({
        status: "verified",
        transaction: {
          hash,
          wallet: swap.account,
          amount: swap.amount,
          blockNumber: receipt.blockNumber,
          timestamp,
        },
      });
    } catch {
      setLookup({
        status: "failed",
        message: "Transaksi belum ditemukan atau jaringan sedang tidak dapat dibaca. Periksa hash lalu coba lagi.",
      });
    }
  }, []);

  const submitLookup = useCallback(
    (value: string) => {
      const normalized = value.trim();
      if (TX_HASH.test(normalized)) {
        void verifyTransaction(normalized as Hex);
        return;
      }
      if (WALLET_ADDRESS.test(normalized)) {
        setLookup({
          status: "failed",
          message: "Pencarian berdasarkan wallet belum tersedia. Gunakan hash transaksi migrasi dari riwayat wallet Anda.",
        });
        return;
      }
      if (normalized) {
        navigate(`/verify/${encodeURIComponent(normalized)}`);
        return;
      }
      setLookup({ status: "failed", message: "Masukkan hash transaksi atau Registry ID." });
    },
    [navigate, verifyTransaction],
  );

  useEffect(() => {
    if (!initial || !TX_HASH.test(initial)) return;
    const timer = window.setTimeout(() => void verifyTransaction(initial as Hex), 0);
    return () => window.clearTimeout(timer);
  }, [initial, verifyTransaction]);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    submitLookup(query);
  }

  const verified = lookup.status === "verified" ? lookup.transaction : null;

  return (
    <div className="page-shell verify-hub">
      <PageHeader
        context="Bukti on-chain"
        title="VERIFIKASI ASETQU"
        description="Periksa transaksi migrasi atau catatan registry langsung dari sumber on-chain yang dikonfigurasi portal."
        meta={
          <>
            <span>{NETWORK_NAME}</span>
            <span>Tanpa login</span>
          </>
        }
      />

      <section className="verify-console" aria-labelledby="verify-search-title">
        <div className="verify-console__intro">
          <p>Proof before persuasion</p>
          <h2 id="verify-search-title">Masukkan bukti yang ingin diperiksa.</h2>
          <p>
            Hash transaksi membuktikan migrasi token. Registry ID memeriksa vendor atau utility
            resmi AsetQu.
          </p>
        </div>
        <form className="verify-search" onSubmit={onSubmit}>
          <label htmlFor="verify-query">Hash transaksi / Registry ID</label>
          <div>
            <input
              id="verify-query"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="0x8a… atau ASETQU_VENDOR_000043"
              spellCheck={false}
              autoComplete="off"
            />
            <Button type="submit" disabled={lookup.status === "loading"}>
              {lookup.status === "loading" ? "MEMERIKSA…" : "VERIFIKASI"}
            </Button>
          </div>
        </form>
      </section>

      {lookup.status === "loading" ? (
        <div className="verification-loading" role="status" aria-live="polite">
          <span aria-hidden />
          <div>
            <strong>MEMERIKSA JARINGAN</strong>
            <p>Menunggu receipt dan event migrasi dari {NETWORK_NAME}.</p>
          </div>
        </div>
      ) : null}

      {lookup.status === "failed" ? (
        <Alert tone="warn" title="BELUM DAPAT DIVERIFIKASI">
          {lookup.message}
        </Alert>
      ) : null}

      {verified ? (
        <ProofStamp
          title="MIGRASI TERVERIFIKASI"
          record={`TX ${shortenAddress(verified.hash, 10, 8)}`}
          actions={
            <>
              <CopyAction value={verified.hash} label="Salin hash" />
              <a href={explorerTx(verified.hash)} target="_blank" rel="noopener noreferrer">
                Lihat transaksi <span aria-hidden>↗</span>
              </a>
              {CLAIM_CONTRACT ? (
                <a href={explorerAddress(CLAIM_CONTRACT)} target="_blank" rel="noopener noreferrer">
                  Lihat kontrak <span aria-hidden>↗</span>
                </a>
              ) : null}
            </>
          }
        >
          <ReviewRow label="Status" emphasis>TERKONFIRMASI</ReviewRow>
          <ReviewRow label="Wallet">
            <code>{verified.wallet}</code>
          </ReviewRow>
          <ReviewRow label="Jumlah" emphasis>
            {formatUnits(verified.amount, TOKEN_DECIMALS)} {TOKEN_SYMBOL}
          </ReviewRow>
          <ReviewRow label="Transaction hash">
            <code>{verified.hash}</code>
          </ReviewRow>
          <ReviewRow label="Block">#{verified.blockNumber.toString()}</ReviewRow>
          {verified.timestamp !== null ? (
            <ReviewRow label="Waktu">{formatTimestamp(verified.timestamp)} WIB</ReviewRow>
          ) : null}
        </ProofStamp>
      ) : null}

      <section className="verify-examples" aria-labelledby="verify-examples-title">
        <h2 id="verify-examples-title">Yang dapat Anda periksa</h2>
        <dl>
          <div>
            <dt>Transaction hash</dt>
            <dd>Memastikan receipt sukses, kontrak tujuan resmi, dan event migrasi valid.</dd>
          </div>
          <div>
            <dt>Registry ID</dt>
            <dd>Memeriksa status vendor atau utility langsung dari UtilityRegistry.</dd>
          </div>
          <div>
            <dt>Alamat kontrak</dt>
            <dd>
              Bandingkan seluruh alamat resmi di halaman <Link to="/contract-addresses">Kontrak</Link>.
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
